import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { campaignsClient } from 'clients/CampaignsClient/CampaignsClient';
import { queryClient } from 'config/queryClient';
import { usePageContext } from 'hooks/shared/usePageContext';
import { PageContext } from 'hooks/shared/usePageContext.types';
import { QueryKey } from 'types/query';
import { statsInitialState } from './SelectedCampaignContext.context';
import { CampaignStats, CampaignTab } from './SelectedCampaignContext.types';

export const useStats = (id: number | undefined): { stats: CampaignStats; isLoading: boolean } => {
  const pageContext = usePageContext();

  const { data, isFetching } = useQuery(
    [QueryKey.selectedCampaignContextUseStats, id],
    async () => {
      if (typeof id !== 'number') throw Error('id cannot be empty');

      if (pageContext === PageContext.RatedCandidates) return;

      const [{ data: campaign }, { data: stats }] = await Promise.all([
        campaignsClient.getCampaign(id),
        campaignsClient.getCampaignCandidatesStats(id),
      ]);

      return { ...stats, [CampaignTab.New]: campaign.total_candidates_count };
    },
    { enabled: typeof id === 'number' && pageContext === PageContext.Campaign },
  );

  return useMemo(() => ({ stats: data || statsInitialState, isLoading: isFetching }), [data, isFetching]);
};

export const setStats = (id: number | undefined, callback: (data: CampaignStats) => CampaignStats) => {
  queryClient.setQueryData([QueryKey.selectedCampaignContextUseStats, id], (data: CampaignStats | undefined) => {
    if (!data) throw Error('Set stats undefined data');

    return callback(data);
  });
};
